import { isEqual } from 'lodash';
import { useRef } from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation, useNavigate } from 'hotel/navigation';

import {
  Gender,
  ReservationRequiredField,
  RosterFilterInput,
  State,
} from '../../generated/graphql';
import { useClientRequiredFieldsAndPod } from '../../hooks/useClientRequiredFieldsAndPod';
import { useUserRole } from '../../hooks/useUserRole';
import { useAppSelector } from '../../store/store';
import getEnumLabel from '../../utilities/getEnumLabel';
import getEnumValues from '../../utilities/getEnumValues';
import {
  getControlFieldLabelForPod,
  getControlFieldPlaceholderForPod,
} from '../../utilities/podBasedFiltering';
import { Button, TealButton } from '../Button';
import {
  TableFilterCheckboxField,
  TableFilterDateRangeField,
  TableFilterReactSelectCreatable,
  TableFilterReactSelectEnum,
  TableFilterSelectField,
  TableFilterTextField,
  formatEnumsToOptions,
  parseValuesToEnums,
} from '../Inputs';

type RosterFilterFormProps = {
  onSubmit: (values: RosterFilterInput) => void;
  initialFilters?: Partial<RosterFilterInput>;
  onClose: () => void;
};

// Custom wrapper for select in sidebar that handles portal
const SidebarSelectEnum = (
  props: React.ComponentProps<typeof TableFilterReactSelectEnum>,
) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Add portal configuration
  return (
    <div ref={containerRef}>
      <TableFilterReactSelectEnum
        {...props}
        menuPortalTarget={
          document.getElementById('roster-filter-portal') || document.body
        }
        menuPosition="fixed"
        menuPlacement="auto"
      />
    </div>
  );
};

// Custom wrapper for date picker in sidebar that handles portal
const SidebarDateRangeField = (
  props: React.ComponentProps<typeof TableFilterDateRangeField>,
) => {
  return (
    <TableFilterDateRangeField {...props} portalId="roster-filter-portal" />
  );
};

const RosterFilterForm = ({
  onSubmit,
  initialFilters,
  onClose,
}: RosterFilterFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rosterRoomAssignmentStatuses, states, genders } = useAppSelector(
    (state) => state.enums,
  );
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();
  const { isOnlyTraveler } = useUserRole();

  // Add loading check for enums - if they're not loaded, don't render the form yet
  const enumsLoaded = rosterRoomAssignmentStatuses && states && genders;

  if (!enumsLoaded) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto">
          <h4 className="mb-6 text-sm font-medium text-gray-500">
            Roster view
          </h4>
          <div className="flex justify-center p-8">
            <div className="text-gray-500">Loading filters...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <h4 className="mb-6 text-sm font-medium text-gray-500">Roster view</h4>
        <Form
          initialValues={initialFilters}
          initialValuesEqual={(obj1, obj2) => isEqual(obj1, obj2)}
          onSubmit={onSubmit}
          render={({ handleSubmit, form }) => (
            <form onSubmit={handleSubmit} className="flex flex-col">
              <div className="flex flex-col gap-y-6 overflow-y-auto px-1">
                <div className="mt-1 flex flex-col gap-y-2 border-b border-gray-200 pb-5">
                  <Field
                    labelText="Status"
                    name="status"
                    component={SidebarSelectEnum}
                    options={rosterRoomAssignmentStatuses || []}
                    isMulti
                    isSearchable={false}
                    hideSelectedOptions={false}
                    parse={parseValuesToEnums}
                    format={formatEnumsToOptions(
                      rosterRoomAssignmentStatuses || [],
                    )}
                    useStatusBadge={false}
                  />
                  <Field
                    labelText="Show duplicate approved only"
                    name="duplicateApproved"
                    component={TableFilterCheckboxField}
                  />
                </div>
                <div className="flex flex-col gap-y-6 border-b border-gray-200 pb-5">
                  <h4 className="text-sm font-medium text-gray-500">
                    Reservation details
                  </h4>
                  <div className="flex w-full gap-x-2">
                    <Field
                      labelText="Check-in"
                      name="checkInDate"
                      component={SidebarDateRangeField}
                      autoComplete="off"
                    />
                    <Field
                      labelText="Check-out"
                      name="checkOutDate"
                      component={SidebarDateRangeField}
                      autoComplete="off"
                    />
                  </div>
                  <Field
                    labelText="Property"
                    name="property"
                    component={TableFilterTextField}
                    placeholder="Enter hotel or property name"
                  />
                  <div className="grid grid-cols-4 gap-x-2">
                    <div className="col-span-3">
                      <Field
                        labelText="City"
                        name="city"
                        component={TableFilterReactSelectCreatable}
                        placeholder="Type a city name and press enter…"
                        className="col-span-2"
                      />
                    </div>
                    <div className="col-span-1">
                      <Field
                        labelText="State"
                        name="state"
                        component={TableFilterSelectField}
                        options={getEnumValues(states || [])}
                        renderOption={(state: State) => {
                          return <option value={state}>{state}</option>;
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex w-full gap-x-2">
                    <Field
                      labelText={getControlFieldLabelForPod(clientPod)}
                      name="orderNumber"
                      component={TableFilterTextField}
                      placeholder={getControlFieldPlaceholderForPod(clientPod)}
                    />
                    <Field
                      labelText="Confirmation #"
                      name="hotelConfirmationNumber"
                      component={TableFilterTextField}
                      placeholder="Enter confirmation #"
                    />
                  </div>

                  {/* Required fields - all fields flow naturally in grid */}
                  <div className="grid grid-cols-2 gap-x-2 gap-y-6">
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.Mission,
                    ) && (
                      <Field
                        labelText="Mission"
                        name="mission"
                        component={TableFilterTextField}
                        placeholder="Enter mission"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.JobNumber,
                    ) && (
                      <Field
                        labelText="Job #"
                        name="jobNumber"
                        component={TableFilterTextField}
                        placeholder="Enter job number"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.JobProjectName,
                    ) && (
                      <Field
                        labelText="Job/Project Name"
                        name="jobProjectName"
                        component={TableFilterTextField}
                        placeholder="Enter job or project name"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.Customer,
                    ) && (
                      <Field
                        labelText="Customer"
                        name="customer"
                        component={TableFilterTextField}
                        placeholder="Enter customer"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.WorkOrder,
                    ) && (
                      <Field
                        labelText="Work Order #"
                        name="workOrder"
                        component={TableFilterTextField}
                        placeholder="Enter work order number"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.CostCenter,
                    ) && (
                      <Field
                        labelText="Cost Center"
                        name="costCenter"
                        component={TableFilterTextField}
                        placeholder="Enter cost center"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.PoOrGl,
                    ) && (
                      <Field
                        labelText="PO or GL"
                        name="poOrGl"
                        component={TableFilterTextField}
                        placeholder="Enter PO or GL"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.TrfNumber,
                    ) && (
                      <Field
                        labelText="TRF #"
                        name="trfNumber"
                        component={TableFilterTextField}
                        placeholder="Enter TRF number"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.OrderedBy,
                    ) && (
                      <Field
                        labelText="Ordered By"
                        name="orderedBy"
                        component={TableFilterTextField}
                        placeholder="Enter who ordered"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.ApproverId,
                    ) && (
                      <Field
                        labelText="Approver ID"
                        name="approverId"
                        component={TableFilterTextField}
                        placeholder="Enter approver ID"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.ContactInfo,
                    ) && (
                      <Field
                        labelText="Contact Info"
                        name="contactInfo"
                        component={TableFilterTextField}
                        placeholder="Enter contact information"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.LocationBase,
                    ) && (
                      <Field
                        labelText="Location/Base"
                        name="locationBase"
                        component={TableFilterTextField}
                        placeholder="Enter location or base"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.FleetNumber,
                    ) && (
                      <Field
                        labelText="Fleet #"
                        name="fleetNumber"
                        component={TableFilterTextField}
                        placeholder="Enter fleet number"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.District,
                    ) && (
                      <Field
                        labelText="District"
                        name="district"
                        component={TableFilterTextField}
                        placeholder="Enter district"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) => field.value === ReservationRequiredField.Crew,
                    ) && (
                      <Field
                        labelText="Crew"
                        name="crew"
                        component={TableFilterTextField}
                        placeholder="Enter crew"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) => field.value === ReservationRequiredField.Well,
                    ) && (
                      <Field
                        labelText="Well"
                        name="well"
                        component={TableFilterTextField}
                        placeholder="Enter well"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.DoorCode,
                    ) && (
                      <Field
                        labelText="Door Code"
                        name="doorCode"
                        component={TableFilterTextField}
                        placeholder="Enter door code"
                      />
                    )}
                    {clientRequiredFields.some(
                      (field) =>
                        field.value === ReservationRequiredField.DepartmentCode,
                    ) && (
                      <Field
                        labelText="Department Code"
                        name="departmentCode"
                        component={TableFilterTextField}
                        placeholder="Enter department code"
                      />
                    )}
                  </div>
                </div>

                {/* Guest Details - GUEST scope fields */}
                {/* Hide guest filters for travelers - they can only see their own data */}
                {!isOnlyTraveler && (
                  <div className="flex flex-col gap-y-6 border-b border-gray-200 pb-5">
                    <h4 className="text-sm font-medium text-gray-500">
                      Guest details
                    </h4>
                    <Field
                      labelText="Guest Name"
                      name="fullName"
                      component={TableFilterTextField}
                      placeholder="Enter guest name"
                    />
                    {/* All other guest fields flow naturally in grid */}
                    <div className="grid grid-cols-2 gap-x-2 gap-y-6">
                      {clientRequiredFields.some(
                        (field) =>
                          field.value === ReservationRequiredField.Rank,
                      ) && (
                        <Field
                          labelText="Rank"
                          name="rank"
                          component={TableFilterTextField}
                          placeholder="Enter rank"
                        />
                      )}
                      {clientRequiredFields.some(
                        (field) =>
                          field.value === ReservationRequiredField.Gender,
                      ) && (
                        <Field
                          labelText="Gender"
                          name="gender"
                          component={TableFilterSelectField}
                          options={getEnumValues(genders || [])}
                          renderOption={(gender: Gender) => {
                            return (
                              <option value={gender}>
                                {getEnumLabel(genders || [], gender)}
                              </option>
                            );
                          }}
                        />
                      )}
                      {clientRequiredFields.some(
                        (field) =>
                          field.value === ReservationRequiredField.DodId,
                      ) && (
                        <Field
                          labelText="DoD ID"
                          name="semiUniqueIdentifier"
                          component={TableFilterTextField}
                          placeholder="Enter DoD ID"
                        />
                      )}
                      {clientRequiredFields.some(
                        (field) =>
                          field.value === ReservationRequiredField.TaskForce,
                      ) && (
                        <Field
                          labelText="Task Force"
                          name="taskForce"
                          component={TableFilterTextField}
                          placeholder="Enter task force"
                        />
                      )}
                      {clientRequiredFields.some(
                        (field) =>
                          field.value ===
                          ReservationRequiredField.EmployeeNumber,
                      ) && (
                        <Field
                          labelText="Employee #"
                          name="employeeNumber"
                          component={TableFilterTextField}
                          placeholder="Enter employee number"
                        />
                      )}
                      {clientRequiredFields.some(
                        (field) =>
                          field.value === ReservationRequiredField.JobTitle,
                      ) && (
                        <Field
                          labelText="Job Title"
                          name="jobTitle"
                          component={TableFilterTextField}
                          placeholder="Enter job title"
                        />
                      )}
                      {clientRequiredFields.some(
                        (field) =>
                          field.value === ReservationRequiredField.UnitCode,
                      ) && (
                        <Field
                          labelText="Unit Code"
                          name="unitCode"
                          component={TableFilterTextField}
                          placeholder="Enter unit code"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop Buttons */}
              <div className="hidden sm:flex sm:justify-end sm:gap-3 sm:pt-5">
                <Button
                  onClick={() => {
                    navigate(location.pathname, { replace: true });
                    form.reset();
                    onClose();
                  }}
                >
                  Clear Filters
                </Button>
                <TealButton type="submit" className="text-sm">
                  Apply Filters
                </TealButton>
              </div>

              {/* Mobile Fixed Bottom Buttons */}
              <div className="absolute inset-x-0 bottom-0 mt-auto w-full border-t border-gray-200 bg-gray-200 p-4 shadow-lg sm:hidden">
                <div className="flex gap-3">
                  <Button
                    className="w-full justify-center"
                    onClick={() => {
                      navigate(location.pathname, { replace: true });
                      form.reset();
                      onClose();
                    }}
                  >
                    Clear Filters
                  </Button>
                  <TealButton
                    type="submit"
                    className="w-full justify-center text-sm"
                  >
                    Apply Filters
                  </TealButton>
                </div>
              </div>
            </form>
          )}
        />
      </div>
    </div>
  );
};

export default RosterFilterForm;
